import React, { useMemo } from 'react';

import { toast } from 'sonner';

import { Toaster } from '@/components/ui/sonner';
import {
  SnackbarContext,
  SnackbarContextType,
} from '@/data/contexts/SnackbarContext';

interface SnackbarProviderProps {
  children: React.ReactNode;
}

type Severity = 'success' | 'error' | 'warning' | 'info';

const DEFAULT_DURATION = 4000;

const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const contextValue = useMemo<SnackbarContextType>(() => {
    const showSnackbar = (
      message: string,
      severity: Severity = 'info',
      duration: number = DEFAULT_DURATION,
    ) => {
      toast[severity](message, { duration });
    };

    return {
      showSnackbar,
      showSuccess: (message, duration) =>
        showSnackbar(message, 'success', duration),
      showError: (message, duration) => showSnackbar(message, 'error', duration),
      showWarning: (message, duration) =>
        showSnackbar(message, 'warning', duration),
      showInfo: (message, duration) => showSnackbar(message, 'info', duration),
      hideSnackbar: () => {
        toast.dismiss();
      },
    };
  }, []);

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Toaster richColors closeButton position="top-right" />
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
